/*
 * Copyright (c) 2020. Sonrai Analytics Ltd.
 * Developed by Warren Dowey
 */

import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppService } from '@app/services/api-services/apps/app.service';
import { AppModel } from '@app/models/app.model';
import { environment } from '@env/environment';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Observable, of, Subscription } from 'rxjs';
import { AppsListService } from '@app/services/api-services/apps/apps-list.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProjectImageService } from '@app/services/api-services/project-images/project-image.service';
import { EventListenerService } from '@app/services/event-listener/event-listener.service';
import { ApiParamsModel } from '@app/models/api-params.model';
import { AuthUserService } from '@app/services/authentication/auth-user-service';
import { SubSink } from '@base/node_modules/subsink/dist/subsink';
import { MaterialSnackbarService } from '@app/services/shared-services/material-snackbar.service';
import { switchMap } from 'rxjs/operators';
import { ProjectDataService } from 'app/services/api-services/project-data/project-data.service';
import { RedisService } from '@app/services/redis/redis.service';
import { v4 as uuid_v4 } from 'uuid';
import { S3Service } from 'app/services/aws-services/s3.service';
import { AuthenticationService } from '@app/services/authentication/authentication.service';

@Component({
  selector: 'app-frame',
  templateUrl: './frame.component.html',
  styleUrls: ['./frame.component.scss'],
})
export class FrameComponent implements OnInit, OnDestroy {
  @Input() dashId = '';
  @Input() slug: string;
  @Input() projectSlug: string;
  @Input() datasetId: string;
  @Input() redisId: string;

  @Input() workflowId: string;
  @Input() step: number;
  @Input() steps: number;
  @Input() dataUrl: string;
  @Input() dataID: string;
  @Input() writeUrl: string;
  @Input() destination: string;
  @Input() fileURL: string[];

  @Output() onCookieCheck = new EventEmitter();
  @Output() onProteomicsComplete = new EventEmitter();

  @ViewChild('modalViewer') modalViewer: any;
  private app: AppModel;
  private appUrl: string;
  public appSafeUrl: SafeResourceUrl = '';
  public loading = true;
  public htmlContent: any;
  public imageId: string;
  public selectedImage: string;
  public modalTitle: string;
  public sessions: [{ sessionId: string; date: string }];
  public selectedSession: string;
  public cookiesEnabled = navigator.cookieEnabled;
  public hasCookie: boolean;
  private cognitoClientId: string;

  @ViewChild('myFrame') myFrame: ElementRef;

  /** care should be noted here that the service isnt called in a modular pattern
   * otherwise the last called frame will overwrite the previous App
   */
  private appService: AppService;

  private appSubscription: Subscription;

  // Subsink to be used in the OnDestroy Lifecycle hook to destroy all active subscriptions
  private subscriptionContainer = new SubSink();

  headers = new HttpHeaders({
    Authorization: 'Bearer ' + this.authUserService.credentials.accessToken,
    clientId: environment.cognitoSettings.clientId,
    Accept: '*/*',
    'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    responseType: 'arraybuffer',
  });
  httpOptions = {
    headers: this.headers,
    responseType: 'text' as 'json',
    withCredentials: true,
  };

  @HostListener('window:message', ['$event']) onMessage(event) {
    this.receiveMessage(event);
  }

  constructor(
    private http: HttpClient,
    private appListService: AppsListService,
    private sanitizer: DomSanitizer,
    private notify: MaterialSnackbarService,
    private ngxService: NgxUiLoaderService,
    private modalService: NgbModal,
    private projectImageService: ProjectImageService,
    private eventListenerService: EventListenerService,
    private authUserService: AuthUserService,
    private projectDataService: ProjectDataService,
    private redisService: RedisService,
    private s3Service: S3Service,
    private authService: AuthenticationService
  ) {}

  /**
   *  Warren Dowey
   *
   *  handleData
   *
   *  called onInit to get the full app details based on the
   * app slug and project-analytics slug passed in as component inputs
   *
   * @returns void
   */
  ngOnInit() {
    // Clear cookie
    document.cookie = 'iframeloaded=;Path=/;Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
    if (this.authService.okta) {
      this.cognitoClientId = environment.oktaSettings.clientId;
    } else {
      this.cognitoClientId = environment.cognitoSettings.clientId;
    }

    this.redisUpload();
    const params = new ApiParamsModel();
    this.appService = new AppService(this.appListService, this.http, this.authUserService);
    this.ngxService.startLoader('frameLoader-' + this.dashId);
    this.subscriptionContainer.sink = this.appSubscription = this.appService.dataSubject.subscribe(
      (data) => {
        if (this.appSubscription) {
          this.appSubscription.unsubscribe();
        }
        this.handleData(data);
        if (this.appSubscription) {
          this.appSubscription.unsubscribe();
        }
      },
      (error) => {
        if (this.appSubscription) {
          this.appSubscription.unsubscribe();
        }
        this.handleError(error);
      }
    );
    this.appService.getAppFromListBySlug(this.slug);

    if (this.workflowId) {
      params.filter = [
        { field: 'user_id', operator: 'eq', value: this.authUserService.user.userId },
        { field: 'workflow_id', operator: 'eq', value: this.workflowId },
        { field: 'step', operator: 'eq', value: this.step },
      ];
    } else {
      params.filter = [
        { field: 'user_id', operator: 'eq', value: this.authUserService.user.userId },
      ];
    }
    this.projectDataService.getData(params);
  }

  redisUpload() {
    if (!this.projectSlug) {
      this.projectSlug = '';
    }
    if (!this.datasetId) {
      this.datasetId = '';
    }
    if (!this.redisId) {
      this.redisId = uuid_v4();
    }
    if (!this.selectedSession) {
      this.selectedSession = this.authUserService.credentials.sessionId;
    }
    this.subscriptionContainer.sink = this.redisService
      .uploadRedis(
        this.redisId,
        this.fileURL,
        this.destination,
        this.authUserService.user.userId,
        environment.product_owner_shortname,
        this.datasetId,
        this.projectSlug,
        this.dataID,
        this.selectedSession,
        this.workflowId,
        this.step,
        this.steps,
        environment.secureServerUrl,
        this.authUserService.credentials.accessToken,
        this.cognitoClientId,
        environment.cognitoSettings.userPoolId,
        environment.cognitoSettings.region
      )
      .subscribe();
  }

  /**
   *  Warren Dowey
   *
   *  handleData
   *
   *  handles the recieving of AppModel data or failure
   * stops the loader and sets up the urls and gets html content to
   * be used later.
   *
   * @returns void
   */
  private handleData(data: any) {
    const app = new AppModel();
    const headers = new HttpHeaders({
      Accept: '*/*',
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    });
    const httpOptions = {
      headers: headers,
      responseType: 'text' as 'json',
    };
    if (data[0]) {
      this.app = app.fromJson(data[0]);
    } else if (typeof data === 'object') {
      this.app = data;
    } else {
      this.appSafeUrl = ''; //
      this.appUrl = '';
      this.htmlContent = 'There was an error finding this app.';
      this.loading = false;
      this.ngxService.stopLoader('frameLoader-' + this.dashId);
      return;
    }
    this.appSafeUrl = this.getAppSafeUrl();
    this.appUrl = this.getAppUrl();
    this.subscriptionContainer.sink = this.http.get(this.appUrl, httpOptions).subscribe(
      (html) => {
        this.htmlContent = html;
        this.loading = false;
        // Set Cookie
        document.cookie = "iframeloaded=true; expires=''";
        this.ngxService.stopLoader('frameLoader-' + this.dashId);
      },
      (error) => {
        // Clear cookie
        document.cookie = 'iframeloaded=;Path=/;Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        this.handleError(error);
      }
    );
  }

  /**
   *  Warren Dowey
   *
   *  handleError
   *
   *  Handle error when the appLoader cannot load an app. Stopping loaded and setting
   * urls as empty strings and outputing a error message
   *
   * @params error: the error passed in
   *
   * @returns void
   */
  private handleError(error: any) {
    this.loading = false;
    this.ngxService.stopLoader('frameLoader-' + this.dashId);
    this.appSafeUrl = '';
    this.appUrl = '';
    this.htmlContent = 'There was an error getting this app';
  }

  /**
   *  Warren Dowey
   *
   *  getAppSafeUrl
   *
   *  called to get the safe url for the app based on
   * app type app slug, project-analytics slug and suffix which is required
   * for html / js based apps.
   *
   * @returns SafeResourceUrl: the xss cleansed url for the app
   */
  getAppSafeUrl(): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.getAppUrl());
  }

  /**
   *  Warren Dowey
   *
   *  getAppSafeUrl
   *
   *  called to get the string url for the app based on
   * app type app slug, project-analytics slug and suffix which is required
   * for html / js based apps. This will be used for the http getting
   * of html output from apps
   *
   * @returns string: the non xss cleansed url for the app
   */
  // getAppUrl(): string {
  //   const appSuffix = '';
  //   let appBaseUrl: string;
  //   let url: string;
  //   switch (this.app.appType) {
  //     case 1:
  //       appBaseUrl = environment.appServerUrl;
  //       break;
  //     default:
  //       appBaseUrl = environment.appServerUrl;
  //   }
  //   url = `${appBaseUrl}${this.app.appSlug}/${appSuffix}?redisId=${this.redisId}`;
  //   // databaseURL
  //   url += `&databaseUrl=${environment.serverUrl}&token=${this.authUserService.credentials.accessToken}&clientId=${environment.cognitoSettings.clientId}&userPoolId=${environment.cognitoSettings.userPoolId}&region=${environment.cognitoSettings.region}`;
  //   return url;
  // }
  getAppUrl(): string {
    const appSuffix = '';
    let appBaseUrl: string;
    let url: string;
    switch (this.app.appType) {
      case 1:
        appBaseUrl = environment.appServerUrl;
        break;
      default:
        appBaseUrl = environment.appServerUrl;
    }
    if (!this.projectSlug) {
      this.projectSlug = '';
    }
    if (!this.datasetId) {
      this.datasetId = '';
    }
    url = `${appBaseUrl}${this.app.appSlug}/${appSuffix}?projectslug=${this.projectSlug}&userId=${this.authUserService.user.userId}&data_id=${this.dataID}&redisId=${this.redisId}`;
    if (this.selectedSession) {
      url += `&sessionId=${this.selectedSession}`;
    } else {
      url += `&sessionId=${this.authUserService.credentials.sessionId}`;
    }
    if (this.workflowId) {
      url += `&workflowId=${this.workflowId}&step=${this.step}&steps=${this.steps}`;
    }
    if (this.destination) {
      url += `&destination_bucket=${this.destination}&rawDataUrls=${this.fileURL}&datasetId=${this.datasetId}`;
    }
    // databaseURL
    url += `&databaseUrl=${environment.secureServerUrl}&token=${this.authUserService.credentials.accessToken}&clientId=${this.cognitoClientId}&userPoolId=${environment.cognitoSettings.userPoolId}&region=${environment.cognitoSettings.region}`;
    return url;
  }

  /**
   *  Warren Dowey
   *
   *  refresh
   *
   *  reloads the iframe content by changing the value of
   * the appSafeUrl a timestamp was added to prevent caching
   *
   * @returns void
   */
  refresh(): void {
    this.loading = true;
    this.appSafeUrl = '';
    this.appSafeUrl = this.getAppSafeUrl();
    this.http.get(this.getAppUrl()).subscribe(
      (data) => {
        this.loading = false;
        // Set Cookie
        document.cookie = "iframeloaded=true; expires=''";
        this.htmlContent = data;
      },
      () => {
        // Clear cookie
        document.cookie = 'iframeloaded=;Path=/;Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
        this.loading = false;
        this.htmlContent = 'There was an error refreshing the app';
      }
    );
  }

  /**
   *  Warren Dowey
   *
   *  receiveMessage
   *
   *  receives the postMessage and filters based on the origin and message content to open the image viewer
   *
   * @params event: a post message object
   */
  receiveMessage(event: any): void {
    event.preventDefault();
    if (environment.appServerUrl === event.origin + '/') {
      if (
        event.data.indexOf('openImage') > -1 &&
        !this.eventListenerService.eventUsed(event.timeStamp)
      ) {
        this.eventListenerService.useEvent(event.timeStamp);
        const message = JSON.parse(event.data);
        this.imageId = message.openImage;
        this.subscriptionContainer.sink = this.projectImageService.dataSubject.subscribe(
          (data) => {
            if (data && data.length) {
              this.selectedImage = this.s3Service.getFileUrl(
                environment.s3ProjectDataPrefix,
                this.projectSlug + '/images/' + data[0].s3_url
              );
              this.modalService.dismissAll();
              this.modalService.open(this.modalViewer, {
                size: 'lg',
                scrollable: true,
                windowClass: 'z-index-modal',
                backdrop: true,
                backdropClass: 'z-index-backdrop',
              });
            } else {
              this.notify.openSnackBar(`Cannot find image.`, 'Close');
              return of(false);
            }
          },
          (error) => {
            this.notify.openSnackBar(`Error retrieving image. Error: ${error}`, 'Close');
          }
        );

        this.projectImageService.getData({
          filter: [{ field: 'project_images_uuid', operator: 'eq', value: this.imageId }],
        });
      }

      if (
        event.data.indexOf('transformedDataLocation') > 1 &&
        !this.eventListenerService.eventUsed(event.timeStamp)
      ) {
        this.eventListenerService.useEvent(event.timeStamp);
        const message = JSON.parse(event.data);
        this.onProteomicsComplete.emit(message);
      }
    }
  }

  onIFrameLoad() {
    // Check if cookies are disabled or iframeloaded cookie is unset
    if (!this.cookiesEnabled || document.cookie.indexOf('iframeloaded=') === -1) {
      this.hasCookie = false;
      // Output hasCookie value
      this.onCookieCheck.emit(this.hasCookie);
    } else {
      this.hasCookie = true;
      // Output hasCookie value
      this.onCookieCheck.emit(this.hasCookie);
    }
  }

  /**
   *  Warren Dowey
   *
   *  ngOnDestroy
   *
   *  Called on destroy, must unsubscribe an subscriptions for adequate
   * garbage collection.
   *
   * @returns void
   */
  ngOnDestroy(): void {
    this.subscriptionContainer.unsubscribe();
  }
}
